import { ContentWithLegendProps } from '@/components';
import { ContentWithLegendGroupByProps } from '@/components/ContentWithLegend/ContentWithLegendGroupBy';

export interface UseTimeSeriesGroupByProps extends Partial<ContentWithLegendGroupByProps> {
  /**
   * When true, the time series chart legend will render grouped series.
   *
   * Currently, the "group by" functionality is only available as a "controlled" feature. This means that
   * consumers are responsible for managing the state, passing it in via `isGrouped`, and accounting
   * for changes via the `onChanged` callback.
   */
  isGrouped?: ContentWithLegendGroupByProps['isGrouped'];

  /**
   * Callback when the state of the group by switch changes. See `isGrouped` for additional information
   * about managing the controlled grouping state.
   */
  onChange?: (isGrouped: boolean) => void;

  /**
   * When true, grouped time series data was successfully calculated, which means the "group by"
   * feature was properly implemented. This is an internal prop that is not exposed to consumers.
   */
  hasTimeSeriesGrouping: boolean;
}

export interface UseTimeSeriesGroupByResponse {
  groupByProps: ContentWithLegendProps['groupByProps'];
}

/**
 * Hook that manages some logic related to the "group by" feature of the time series chart legend.
 */
export const useTimeSeriesLegendGroupBy = ({
  show,
  label,
  hasTimeSeriesGrouping,
  onChange: onChangeControlled,
  isGrouped: isGroupedControlled,
}: UseTimeSeriesGroupByProps): UseTimeSeriesGroupByResponse => {
  const isControlled = typeof isGroupedControlled !== 'undefined';

  const isGrouped = isControlled ? isGroupedControlled : false;
  const onChange = isControlled ? onChangeControlled : undefined;

  return {
    groupByProps: {
      isGrouped,
      onChange,
      show: !!show && hasTimeSeriesGrouping,
      label,
    },
  };
};
